import { create } from 'zustand';
import {
  getOnboardingProgress,
  updateOnboardingProgress,
  trackOnboardingEvent,
  updateBusinessType,
  getChecklistStatus,
} from '../services/onboarding.service';
import type {
  TourStatus,
  OnboardingProgressData,
  ChecklistStatusData,
  TourDefinition,
  TourExitReason,
} from '../models/tour.interfaces';
import type { TourModule } from '../models/onboarding.constants';
import { CHECKLIST_ITEMS } from '../models/onboarding.constants';
import {
  calculateCompletionPct as calcPct,
  checklistToTourMap,
  hasActiveEmissionRuc,
  isChecklistItemDone,
} from '../models/checklistProgress';
import { getData } from '../../../services/constServices';
import { API_V2 } from '@Models/GWDomain';

interface OnboardingState {
  // State
  isLoaded: boolean;
  isLoading: boolean;
  businessType: number;
  videoSeen: boolean;
  checklist: {
    employees: boolean;
    services: boolean;
    products: boolean;
    cashRegister: boolean;
  };
  tours: Record<TourModule, TourStatus>;
  firstSale: boolean;
  completionPct: number;
  checklistStatus: ChecklistStatusData | null;
  hasBillingRuc: boolean;
  skipped: boolean;

  // Guided checklist flow
  isChecklistGuided: boolean; // true when following the guided onboarding sequence
  highlightedChecklistItem: string | null; // key of item to highlight with pulse
  showChecklistIntro: boolean; // show spotlight on sidebar for first time

  // UI
  isChecklistOpen: boolean;
  activeTour: TourModule | null;
  activeTourStep: number;
  /** Tour dinámico (guías de Adri): no persiste progreso en /onboarding/progress */
  activeTourDefinition: TourDefinition | null;
  showVideoModal: boolean;
  showBusinessTypeModal: boolean;
  showCelebration: { type: string; message: string } | null;

  // Actions
  initialize: () => Promise<void>;
  setBusinessType: (type: number) => Promise<void>;
  markVideoSeen: () => Promise<void>;
  completeChecklistTask: (task: string) => Promise<void>;
  startChecklistTour: (checklistKey: string) => void;
  startTour: (module: TourModule) => void;
  advanceTourStep: () => void;
  previousTourStep: () => void;
  completeTour: (module: TourModule) => Promise<void>;
  skipTour: (module: TourModule) => Promise<void>;
  endTour: () => void;
  startDynamicTour: (
    def: TourDefinition,
    cbs?: { onFinish?: () => void; onExit?: (reason: TourExitReason) => void },
  ) => void;
  endDynamicTour: (reason: TourExitReason | 'finish') => void;
  trackEvent: (event: {
    eventType: string;
    module?: string;
    stepNumber?: number;
    metadata?: any;
  }) => void;
  toggleChecklist: () => void;
  skipOnboarding: () => Promise<void>;
  refreshChecklistStatus: () => Promise<void>;
  dismissCelebration: () => void;
  dismissChecklistIntro: () => void;
}

const tourStatusFromNumber = (val: number): TourStatus => {
  if (val === 1) return 'completed';
  if (val === 2) return 'skipped';
  return 'pending';
};

const tourFieldMap: Record<TourModule, string> = {
  employees: 'tourEmployees',
  services: 'tourServices',
  products: 'tourProducts',
  customers: 'tourCustomers',
  reservations: 'tourReservations',
  pos: 'tourPos',
};

// Map tour module back to checklist key
const tourToChecklistMap: Record<string, string> = {
  employees: 'employees',
  services: 'services',
  products: 'products',
  pos: 'firstSale',
};

const calculateCompletionPct = (
  checklist: OnboardingState['checklist'],
  checklistStatus: ChecklistStatusData | null,
  tours: OnboardingState['tours'],
  firstSale: boolean,
  hasBillingRuc: boolean,
  skipped = false,
): number => {
  if (skipped) return 100;
  return calcPct({ checklist, checklistStatus, tours, firstSale, hasBillingRuc });
};

const skipFlagKey = (): string => {
  try {
    const token = localStorage.getItem('access_token') || '';
    const payload = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
    return `prikly_onboarding_skipped:${payload.idempresa ?? payload.companyId ?? ''}:${payload.idlocal ?? ''}`;
  } catch {
    return 'prikly_onboarding_skipped';
  }
};
const readSkippedFlag = (): boolean => {
  try {
    return localStorage.getItem(skipFlagKey()) === '1';
  } catch {
    return false;
  }
};
const writeSkippedFlag = () => {
  try {
    localStorage.setItem(skipFlagKey(), '1');
  } catch {
    /* sin almacenamiento: queda solo en la sesión */
  }
};

/** RUC activo para emitir (OSE vigente o PSE activo); un fallo de red cuenta como "no". */
const fetchHasBillingRuc = async (): Promise<boolean> => {
  try {
    const res = await getData(`${API_V2}/billing/certificates`);
    return res.isSuccess ? hasActiveEmissionRuc(res.data as any[]) : false;
  } catch {
    return false;
  }
};

const getNextPendingChecklistItem = (
  checklist: OnboardingState['checklist'],
  checklistStatus: ChecklistStatusData | null,
  tours: OnboardingState['tours'],
  firstSale: boolean,
  hasBillingRuc: boolean,
  currentKey?: string,
): string | null => {
  let foundCurrent = !currentKey;
  for (const item of CHECKLIST_ITEMS) {
    if (item.key === currentKey) {
      foundCurrent = true;
      continue;
    }
    if (!foundCurrent) continue;
    if (!isChecklistItemDone(item.key, { checklist, checklistStatus, tours, firstSale, hasBillingRuc })) {
      return item.key;
    }
  }
  return null;
};

export const useOnboardingStore = create<OnboardingState>((set, get) => ({
  // Initial state
  isLoaded: false,
  isLoading: false,
  businessType: 0,
  videoSeen: false,
  checklist: {
    employees: false,
    services: false,
    products: false,
    cashRegister: false,
  },
  tours: {
    employees: 'pending',
    services: 'pending',
    products: 'pending',
    customers: 'pending',
    reservations: 'pending',
    pos: 'pending',
  },
  firstSale: false,
  completionPct: 0,
  checklistStatus: null,
  hasBillingRuc: false,
  skipped: false,

  isChecklistGuided: false,
  highlightedChecklistItem: null,
  showChecklistIntro: false,

  isChecklistOpen: false,
  activeTour: null,
  activeTourStep: 0,
  activeTourDefinition: null,
  showVideoModal: false,
  showBusinessTypeModal: false,
  showCelebration: null,

  initialize: async () => {
    if (get().isLoading) return;
    set({ isLoading: true });

    try {
      const [progressRes, checklistRes, hasBillingRuc] = await Promise.all([
        getOnboardingProgress(),
        getChecklistStatus(),
        fetchHasBillingRuc(),
      ]);

      if (progressRes.isSuccess && progressRes.data) {
        const p = progressRes.data as OnboardingProgressData;
        const checklistData = checklistRes.isSuccess
          ? (checklistRes.data as ChecklistStatusData)
          : null;

        const parsedChecklist = {
            employees: !!p.checklistEmployees,
            services: !!p.checklistServices,
            products: !!p.checklistProducts,
            cashRegister: !!p.checklistCashregister,
          };
        const parsedTours = {
            employees: tourStatusFromNumber(p.tourEmployees),
            services: tourStatusFromNumber(p.tourServices),
            products: tourStatusFromNumber(p.tourProducts),
            customers: tourStatusFromNumber(p.tourCustomers),
            reservations: tourStatusFromNumber(p.tourReservations),
            pos: tourStatusFromNumber(p.tourPos),
          };
        const parsedFirstSale = !!p.firstSale;
        // "Omitir la guía" es una acción explícita (se recuerda por empresa en este
        // navegador); omitir tours sueltos NO completa la tarea de facturación.
        const skipped = readSkippedFlag();
        const localPct = calculateCompletionPct(parsedChecklist, checklistData, parsedTours, parsedFirstSale, hasBillingRuc, skipped);

        set({
          isLoaded: true,
          isLoading: false,
          businessType: p.businessType,
          videoSeen: !!p.videoSeen,
          checklist: parsedChecklist,
          tours: parsedTours,
          firstSale: parsedFirstSale,
          completionPct: localPct,
          hasBillingRuc,
          skipped,
          checklistStatus: checklistData,
          showVideoModal: !p.videoSeen,
          showBusinessTypeModal: !!p.videoSeen && p.businessType === 0,
        });
      } else {
        set({ isLoaded: true, isLoading: false });
      }
    } catch {
      set({ isLoaded: true, isLoading: false });
    }
  },

  setBusinessType: async (type: number) => {
    // After selecting business type, open sidebar with guided intro
    set({
      businessType: type,
      showBusinessTypeModal: false,
      isChecklistOpen: true,
      isChecklistGuided: true,
      showChecklistIntro: true,
      highlightedChecklistItem: 'employees',
    });
    await updateBusinessType(type);
    get().trackEvent({ eventType: 'business_type_selected' });
  },

  markVideoSeen: async () => {
    set({ videoSeen: true, showVideoModal: false });
    const state = get();
    if (state.businessType === 0) {
      set({ showBusinessTypeModal: true });
    }
    await updateOnboardingProgress({ videoSeen: 1 });
    get().trackEvent({ eventType: 'video_completed' });
  },

  completeChecklistTask: async (task: string) => {
    const fieldMap: Record<string, string> = {
      employees: 'checklistEmployees',
      services: 'checklistServices',
      products: 'checklistProducts',
      cashRegister: 'checklistCashregister',
    };

    const field = fieldMap[task];
    if (!field) return;

    set((s) => ({
      checklist: { ...s.checklist, [task]: true },
    }));

    await updateOnboardingProgress({ [field]: 1 });
    get().trackEvent({
      eventType: 'checklist_task_completed',
      module: task,
    });
  },

  startChecklistTour: (checklistKey: string) => {
    const tourModule = checklistToTourMap[checklistKey];
    if (!tourModule) return;

    set({
      isChecklistOpen: false,
      isChecklistGuided: true,
      showChecklistIntro: false,
      highlightedChecklistItem: null,
    });

    // Navigate is handled by the component, just start the tour after a delay
    setTimeout(() => {
      set({ activeTour: tourModule, activeTourStep: 0 });
      get().trackEvent({
        eventType: 'tour_step_started',
        module: tourModule,
        stepNumber: 1,
      });
    }, 800);
  },

  startTour: (module: TourModule) => {
    // When auto-started via useTour hook, ensure guided flow is off
    // (guided flow is only set by startChecklistTour from the sidebar)
    set({ activeTour: module, activeTourStep: 0, isChecklistGuided: false });
    get().trackEvent({
      eventType: 'tour_step_started',
      module,
      stepNumber: 1,
    });
  },

  advanceTourStep: () => {
    set((s) => {
      const nextStep = s.activeTourStep + 1;
      if (s.activeTour) {
        get().trackEvent({
          eventType: 'tour_step_completed',
          module: s.activeTour,
          stepNumber: s.activeTourStep + 1,
        });
      }
      return { activeTourStep: nextStep };
    });
  },

  previousTourStep: () => {
    set((s) => ({
      activeTourStep: Math.max(0, s.activeTourStep - 1),
    }));
  },

  completeTour: async (module: TourModule) => {
    if (get().activeTourDefinition) {
      get().endDynamicTour('finish');
      return;
    }
    const checklistKey = tourToChecklistMap[module];

    // Mark tour as completed
    set((s) => ({
      tours: { ...s.tours, [module]: 'completed' as TourStatus },
      activeTour: null,
      activeTourStep: 0,
    }));

    // Save to backend
    const field = tourFieldMap[module];
    await updateOnboardingProgress({ [field]: 1 });
    get().trackEvent({ eventType: 'tour_completed', module });

    // Also mark corresponding checklist task
    if (checklistKey && checklistKey !== 'firstSale') {
      await get().completeChecklistTask(checklistKey);
    }
    if (checklistKey === 'firstSale') {
      set({ firstSale: true });
      await updateOnboardingProgress({ firstSale: 1 });
    }

    // Refresh checklist status from backend
    await get().refreshChecklistStatus();

    // Recalculate completion percentage locally
    const stateAfterRefresh = get();
    const newPct = calculateCompletionPct(
      stateAfterRefresh.checklist,
      stateAfterRefresh.checklistStatus,
      stateAfterRefresh.tours,
      stateAfterRefresh.firstSale,
      stateAfterRefresh.hasBillingRuc,
      stateAfterRefresh.skipped,
    );
    set({ completionPct: newPct });
    // Sync to backend
    updateOnboardingProgress({ completionPct: newPct }).catch(() => {});

    // Find next pending item and reopen sidebar
    const updatedState = get();
    const nextItem = getNextPendingChecklistItem(
      updatedState.checklist,
      updatedState.checklistStatus,
      updatedState.tours,
      updatedState.firstSale,
      updatedState.hasBillingRuc,
      checklistKey,
    );

    if (nextItem) {
      // Show celebration briefly, then open sidebar with next item highlighted
      set({
        showCelebration: {
          type: 'task_completed',
          message: '¡Tarea completada!',
        },
      });
      setTimeout(() => {
        set({
          isChecklistOpen: true,
          highlightedChecklistItem: nextItem,
          showCelebration: null,
        });
      }, 2000);
    } else {
      // All done! Big celebration
      set({
        showCelebration: {
          type: 'onboarding_completed',
          message: '¡Felicidades! Has configurado tu negocio',
        },
      });
    }

  },

  skipTour: async (module: TourModule) => {
    if (get().activeTourDefinition) {
      get().endDynamicTour('user');
      return;
    }
    const state = get();
    const checklistKey = tourToChecklistMap[module];
    const isGuidedFlow = state.isChecklistGuided;

    set((s) => ({
      tours: { ...s.tours, [module]: 'skipped' as TourStatus },
      activeTour: null,
      activeTourStep: 0,
    }));

    const field = tourFieldMap[module];
    await updateOnboardingProgress({ [field]: 2 });
    get().trackEvent({ eventType: 'tour_skipped', module });

    // If in guided flow, move to next item
    if (isGuidedFlow) {
      const updatedState = get();
      const nextItem = getNextPendingChecklistItem(
        updatedState.checklist,
        updatedState.checklistStatus,
        updatedState.tours,
        updatedState.firstSale,
        updatedState.hasBillingRuc,
        checklistKey,
      );

      if (nextItem) {
        setTimeout(() => {
          set({
            isChecklistOpen: true,
            highlightedChecklistItem: nextItem,
          });
        }, 500);
      } else {
        set({ isChecklistGuided: false });
      }
    }
  },

  endTour: () => {
    set({ activeTour: null, activeTourStep: 0 });
  },

  startDynamicTour: (def, cbs) => {
    set({
      activeTour: null,
      activeTourStep: 0,
      activeTourDefinition: {
        ...def,
        onFinish: cbs?.onFinish ?? def.onFinish,
        onExit: cbs?.onExit ?? def.onExit,
      },
    });
  },

  endDynamicTour: (reason) => {
    const def = get().activeTourDefinition;
    if (!def) return;
    set({ activeTourDefinition: null, activeTourStep: 0 });
    if (reason === 'finish') def.onFinish?.();
    else def.onExit?.(reason);
  },

  trackEvent: (event) => {
    trackOnboardingEvent(event).catch(() => {});
  },

  toggleChecklist: () => {
    set((s) => ({
      isChecklistOpen: !s.isChecklistOpen,
      showChecklistIntro: false,
    }));
  },

  skipOnboarding: async () => {
    const currentTours = get().tours;
    writeSkippedFlag();

    // Los tours pendientes pasan a "skipped"; los ya completados se respetan
    const skippedTours = Object.fromEntries(
      Object.entries(currentTours).map(([k, v]) => [k, v === 'pending' ? 'skipped' : v]),
    ) as OnboardingState['tours'];

    set({
      tours: skippedTours,
      firstSale: true,
      skipped: true,
      completionPct: 100,
      isChecklistOpen: false,
      isChecklistGuided: false,
      showChecklistIntro: false,
      highlightedChecklistItem: null,
      activeTour: null,
      activeTourStep: 0,
    });

    const payload: Record<string, number> = { firstSale: 1 };
    for (const [module, field] of Object.entries(tourFieldMap)) {
      if (currentTours[module as TourModule] === 'pending') payload[field] = 2;
    }
    await updateOnboardingProgress(payload);
    get().trackEvent({ eventType: 'onboarding_skipped' });
  },

  refreshChecklistStatus: async () => {
    const [res, hasBillingRuc] = await Promise.all([getChecklistStatus(), fetchHasBillingRuc()]);
    if (res.isSuccess) {
      const checklistData = res.data as ChecklistStatusData;
      set({ checklistStatus: checklistData, hasBillingRuc });
      // Recalculate completion percentage
      const s = get();
      const newPct = calculateCompletionPct(s.checklist, checklistData, s.tours, s.firstSale, hasBillingRuc, s.skipped);
      set({ completionPct: newPct });
    }
  },

  dismissCelebration: () => {
    set({ showCelebration: null });
  },

  dismissChecklistIntro: () => {
    set({ showChecklistIntro: false });
  },
}));
